import {
  Button,
  Chip,
  Popover,
  Spinner,
  Table,
  Toolbar,
  Tooltip,
  Typography,
} from "@heroui/react";
import { Icon } from "@iconify/react";

import { DateRowValue } from "@/components/common/empty-row-value";
import { VariantAttribute } from "@/types/product";

interface AttributeRowProps {
  attribute: VariantAttribute;
  deletingId: string | null;
  isDeleting: boolean;
  setDeletingId: (id: string | null) => void;
  onDelete: (attribute: VariantAttribute) => void;
  onEdit: () => void;
}

export const AttributeRow = ({
  attribute,
  deletingId,
  isDeleting,
  setDeletingId,
  onDelete,
  onEdit,
}: AttributeRowProps) => {
  const isAttributeDeleting = isDeleting && deletingId === attribute.id;
  const isOpen = deletingId === attribute.id;

  return (
    <>
      <Table.Cell className="before:bg-transparent overflow-hidden">
        <div className="flex flex-col leading-tight">
          <Typography type="h6" className="truncate">
            {attribute.name}
          </Typography>
          {attribute.description && (
            <Typography
              type="body-sm"
              className="text-muted truncate max-w-[200px]"
            >
              {attribute.description}
            </Typography>
          )}
        </div>
      </Table.Cell>
      <Table.Cell className="before:bg-transparent overflow-hidden">
        {attribute.options?.length ? (
          <div className="flex gap-1 flex-wrap max-w-[260px]">
            {attribute.options.slice(0, 3).map((opt) => (
              <Chip key={opt.id} color="default" size="sm" variant="soft">
                {opt.name}
              </Chip>
            ))}
            {attribute.options.length > 3 && (
              <Chip color="default" size="sm" variant="soft">
                +{attribute.options.length - 3}
              </Chip>
            )}
          </div>
        ) : (
          <Typography className="text-muted text-sm">-</Typography>
        )}
      </Table.Cell>
      <Table.Cell className="before:bg-transparent overflow-hidden">
        <DateRowValue value={attribute.createdAt} />
      </Table.Cell>
      <Table.Cell className="before:bg-transparent overflow-hidden">
        <Toolbar
          aria-label="Attribute actions"
          className="flex justify-end gap-0.5 w-full"
        >
          <Tooltip delay={300}>
            <Tooltip.Trigger>
              <Button
                isIconOnly
                aria-label="Edit"
                size="sm"
                variant="tertiary"
                onPress={onEdit}
              >
                <Icon icon="solar:pen-linear" />
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content>Edit</Tooltip.Content>
          </Tooltip>
          <Popover
            isOpen={isOpen}
            onOpenChange={(open) => setDeletingId(open ? attribute.id : null)}
          >
            <Popover.Trigger>
              <Button
                isIconOnly
                aria-label="Delete"
                isDisabled={isDeleting}
                isPending={isAttributeDeleting}
                size="sm"
                variant="danger-soft"
              >
                {({ isPending }) => (
                  <>
                    {isPending ? (
                      <Spinner color="current" size="sm" />
                    ) : (
                      <Icon icon="solar:trash-bin-trash-linear" />
                    )}
                  </>
                )}
              </Button>
            </Popover.Trigger>
            <Popover.Content placement="bottom end">
              <Popover.Dialog>
                <div className="flex flex-col gap-3 p-4 max-w-[280px]">
                  <div className="flex flex-col gap-1">
                    <Typography className="text-sm font-medium text-foreground">
                      Delete {attribute.name}?
                    </Typography>
                    <Typography className="text-xs text-muted">
                      Products using this attribute will lose their values.
                    </Typography>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      isDisabled={isDeleting}
                      size="sm"
                      variant="tertiary"
                      onPress={() => setDeletingId(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      isDisabled={isDeleting}
                      isPending={isAttributeDeleting}
                      size="sm"
                      variant="danger"
                      onPress={() => onDelete(attribute)}
                    >
                      {({ isPending }) => (
                        <>
                          {isPending && <Spinner color="current" size="sm" />}
                          Delete
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Popover.Dialog>
            </Popover.Content>
          </Popover>
        </Toolbar>
      </Table.Cell>
    </>
  );
};
